import { Ref } from "react";
import { useGetContractDeviationUnits } from "@/services/ContractDeviations";
import { CreateContractWizard } from "../helpers/helpers";
import { ContractDeviation } from "@/types";
import { Grid } from "@mantine/core";
import { useTranslation } from "react-i18next";
import EditableCheckboxList from "@/components/atoms/EditableCheckboxList";
import Text from "@/components/atoms/Text";

interface Props {
  onValidationSuccess: (data: any) => void;
  onChangeWizardData: (changed: any) => void;
  wizardData: CreateContractWizard;
  ref?: Ref<HTMLFormElement>;
}

function Deviations({ onValidationSuccess, onChangeWizardData, wizardData, ref }: Props) {
  const { t } = useTranslation();
  const { data: deviationUnits } = useGetContractDeviationUnits();

  return (
    <>
      <form
        ref={ref}
        // do not remove the e.preventDefault() as it is necessary to prevent the form from submitting and refreshing the page when the user clicks the next button. The onValidationSuccess function will be called to handle the form submission logic and move to the next step in the wizard.
        onSubmit={(e) => {
          e.preventDefault();
          onValidationSuccess(wizardData);
        }}
        data-testid={"contract-deviation-form"}
      >
        <Grid>
          <Grid.Col span={12}>
            <Text size="xl" data-testid={"deviations"}>
              {t("common:deviations")}
            </Text>
          </Grid.Col>
          {wizardData.contractDeviationOptions.length > 0 && deviationUnits ? (
            <Grid.Col span={12}>
              <EditableCheckboxList
                items={wizardData.contractDeviationOptions}
                editableField="price"
                gridSpan={{ base: 6 }}
                getCheckboxLabel={(s) => s.name}
                isChecked={(s) =>
                  wizardData.contractDeviations.some((cd: ContractDeviation) => cd.contractDeviationTypeId === s.id)
                }
                onCheckboxToggle={(item, isChecked) => {
                  const deviationUnit = deviationUnits.find((du) => du.id === item.priceUnit);
                  const newDeviation: ContractDeviation = {
                    contractDeviationTypeId: item.id,
                    price: item.price,
                    priceUnit: deviationUnit!,
                    currency: item.currency,
                  };
                  const selectedDeviations = wizardData.contractDeviations;
                  onChangeWizardData({
                    contractDeviations: isChecked
                      ? [...selectedDeviations, newDeviation]
                      : selectedDeviations.filter((s: ContractDeviation) => s.contractDeviationTypeId !== item.id),
                  });
                }}
                getTextInputValue={(s) => s.price}
                getTextInputSuffix={(s) => {
                  const priceUnit = deviationUnits.find((du) => du.id === s.priceUnit)
                    ? t("enumerables:priceUnit." + deviationUnits.find((du) => du.id === s.priceUnit)?.code)
                    : "";
                  return `${s.currency}/${priceUnit}`;
                }}
                onTextInputChange={(item, newValue) =>
                  onChangeWizardData({
                    contractDeviationOptions: wizardData.contractDeviationOptions.map((cd) => {
                      if (cd.id === item.id) {
                        const parsed = parseFloat(newValue);
                        return { ...cd, price: isNaN(parsed) ? 0 : parsed };
                      }
                      return cd;
                    }),
                    contractDeviations: wizardData.contractDeviations.map((s) => {
                      if (s.contractDeviationTypeId === item.id) {
                        const parsed = parseFloat(newValue);
                        return { ...s, price: isNaN(parsed) ? 0 : parsed };
                      }
                      return s;
                    }),
                  })
                }
              />
            </Grid.Col>
          ) : (
            <Grid.Col span={12}>
              <Text data-testid={"noDeviationsAdded"}>{t("contracts:contractDeviation.noDeviationsAdded")}</Text>
            </Grid.Col>
          )}
          <Grid.Col span={12}>
            <Text color="secondary" data-testid={"deviationStepInfo"}>
              {t("contracts:contractDeviation.deviationStepInfo")}
            </Text>
          </Grid.Col>
        </Grid>
      </form>
    </>
  );
}

export default Deviations;
